import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { CalendarBlank } from 'phosphor-react';

import {
  PeriodoPreset,
  PeriodoRange,
  buildTodosPeriodo,
  computePresetRange,
} from '../../../../analiseBots/components/PeriodoFlowFilter';

interface Props {
  value: PeriodoRange;
  // eslint-disable-next-line no-unused-vars
  onChange: (range: PeriodoRange) => void;
  /** Inclui a pill "Todos" (sem filtro de data) à esquerda. Default true no ContaCil. */
  incluirTodos?: boolean;
}

// Presets fixos (Todos/Custom têm tratamento próprio).
const PRESETS: Array<{ key: PeriodoPreset; label: string }> = [
  { key: 'hoje', label: 'Hoje' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
];

// Mesma normalização local do PeriodoFlowFilter (evita o toISOString() estourar
// o dia em UTC-3). Só formatação de exibição dos inputs — não é regra de range.
const isoDateOnly = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(
    2,
    '0',
  )}`;

const fmtDM = (d: Date) =>
  `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;

/**
 * Segmented control de período na linguagem glass do módulo Contábil.
 * Mesma gramática visual do toggle Recentes/Antigos (extratos/index.tsx) e do
 * toggle Razão/CNPJ/Id (EmpresaPicker): container translúcido, pill ativa
 * branca + sombra, accent #1B88F5.
 * REUSA computePresetRange/buildTodosPeriodo do PeriodoFlowFilter — zero
 * duplicação da lógica de datas.
 */
export function PeriodoSegment({ value, onChange, incluirTodos = true }: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [customInit, setCustomInit] = useState(() =>
    value.date_init ? isoDateOnly(new Date(value.date_init)) : '',
  );
  const [customEnd, setCustomEnd] = useState(() =>
    value.date_end ? isoDateOnly(new Date(value.date_end)) : '',
  );

  // Sincroniza inputs do popover com o range ativo (mesma estratégia de deps
  // do PeriodoFlowFilter).
  useEffect(() => {
    if (value.date_init && value.date_end) {
      setCustomInit(isoDateOnly(new Date(value.date_init)));
      setCustomEnd(isoDateOnly(new Date(value.date_end)));
    }
  }, [value.date_init, value.date_end, value.preset]);

  const handlePreset = (preset: PeriodoPreset) =>
    onChange(preset === 'todos' ? buildTodosPeriodo() : computePresetRange(preset));

  const handleApplyCustom = () => {
    if (!customInit || !customEnd) return;
    const init = new Date(`${customInit}T00:00:00`);
    const end = new Date(`${customEnd}T23:59:59`);
    onChange({ preset: 'custom', date_init: init.toISOString(), date_end: end.toISOString() });
    onClose();
  };

  const customAtivo = value.preset === 'custom';
  const customLabel =
    customAtivo && value.date_init && value.date_end
      ? `${fmtDM(new Date(value.date_init))} a ${fmtDM(new Date(value.date_end))}`
      : 'Personalizar';

  // Pills: gêmeas do toggle Recentes/Antigos da mesma tela — container
  // rgba(15,23,42,0.04), pill ativa branca + sombra, accent #1B88F5.
  const pill = (ativo: boolean) => ({
    size: 'xs' as const,
    h: '26px',
    px: 3,
    borderRadius: 'full' as const,
    variant: 'unstyled' as const,
    fontSize: '12px',
    fontWeight: '600' as const,
    display: 'inline-flex',
    alignItems: 'center',
    bg: ativo ? 'white' : 'transparent',
    color: ativo ? '#1B88F5' : '#64748B',
    boxShadow: ativo ? '0 1px 4px -1px rgba(15,23,42,0.15)' : undefined,
    _hover: ativo ? {} : { color: '#0F172A' },
  });

  return (
    <Flex align="center" gap="2px" bg="rgba(15,23,42,0.04)" borderRadius="full" p="3px" h="32px">
      {incluirTodos && (
        <Button {...pill(value.preset === 'todos')} onClick={() => handlePreset('todos')}>
          Todos
        </Button>
      )}
      {PRESETS.map(p => (
        <Button key={p.key} {...pill(value.preset === p.key)} onClick={() => handlePreset(p.key)}>
          {p.label}
        </Button>
      ))}
      <Popover placement="bottom-end" isOpen={isOpen} onOpen={onOpen} onClose={onClose} isLazy>
        <PopoverTrigger>
          <Button {...pill(customAtivo)} gap={1}>
            <CalendarBlank size={13} weight="bold" />
            {customLabel}
          </Button>
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            w="260px"
            borderRadius="xl"
            borderColor="rgba(15,23,42,0.06)"
            boxShadow="0 12px 40px -12px rgba(27,136,245,0.25), 0 4px 12px -4px rgba(15,23,42,0.08)"
            _focus={{ outline: 'none' }}
          >
            <PopoverArrow />
            <PopoverBody>
              <VStack spacing={3} align="stretch">
                <Text fontSize="10px" fontWeight="600" color="#64748B" textTransform="uppercase">
                  Período personalizado
                </Text>
                <Box>
                  <Text fontSize="10px" fontWeight="600" color="#64748B" mb={1}>
                    De
                  </Text>
                  <Input
                    type="date"
                    size="sm"
                    borderRadius="lg"
                    bg="white"
                    value={customInit}
                    onChange={e => setCustomInit(e.target.value)}
                  />
                </Box>
                <Box>
                  <Text fontSize="10px" fontWeight="600" color="#64748B" mb={1}>
                    Até
                  </Text>
                  <Input
                    type="date"
                    size="sm"
                    borderRadius="lg"
                    bg="white"
                    value={customEnd}
                    onChange={e => setCustomEnd(e.target.value)}
                  />
                </Box>
                <Button
                  size="sm"
                  borderRadius="lg"
                  bg="#1B88F5"
                  color="white"
                  _hover={{ bg: '#1574d6' }}
                  isDisabled={!customInit || !customEnd || customEnd < customInit}
                  onClick={handleApplyCustom}
                >
                  Aplicar
                </Button>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </Flex>
  );
}
